import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useAnimationControls, useMotionValue, useMotionTemplate } from 'framer-motion';
import { auth, googleProvider } from '../../../firebaseAuth';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  browserPopupRedirectResolver,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut
} from 'firebase/auth';
import { useI18n } from '../../../i18n';
import {
  ChevronRight, Eye, EyeOff, Mail, Lock, UserPlus, LogIn,
  MailCheck, RefreshCw, ArrowLeft, ArrowUpRight, Check, X, Flag
} from 'lucide-react';
import { isMobileDevice, sceneState, kickScene } from './loginShared';

/* Password strength meter */
const PW_STRENGTH = [
  { label: '', color: 'transparent' },
  { label: 'Słabe', color: '#ef4444' },
  { label: 'Średnie', color: '#f59e0b' },
  { label: 'Dobre', color: '#3b82f6' },
  { label: 'Silne', color: '#22c55e' },
];

function getPasswordScore(pw) {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 6) score++;
  if (pw.length >= 10) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(score, 4);
}

function PasswordStrength({ password, t }) {
  const score = getPasswordScore(password);
  const meta = PW_STRENGTH[score];
  const labelColor = meta.color === 'transparent' ? 'var(--text-muted)' : meta.color;
  return (
    <div className="flex items-center gap-2 mt-2">
      <div className="flex-1 flex gap-1">
        {[1, 2, 3, 4].map(i => (
          <span
            key={i}
            className="h-1 flex-1 rounded-full"
            style={{
              backgroundColor: i <= score ? meta.color : 'var(--surface-200)',
              transition: 'background-color 0.3s ease',
            }}
          />
        ))}
      </div>
      <span className="text-[10px] font-semibold min-w-[46px] text-right" style={{ color: labelColor }}>
        {meta.label ? t(meta.label) : ''}
      </span>
    </div>
  );
}

// Phones and iOS PWAs redirect instead: a popup there is easily blocked and,
// in an iOS PWA, opens outside the app and cannot report back. Android PWAs keep
// the popup: the redirect leaves the app window and comes back without the
// pending sign-in, while the popup shares the app's storage on mykart.pl.
const isStandalone = typeof window !== 'undefined' && (
  window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true);
const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
const useGoogleRedirect = typeof window !== 'undefined' && !(isAndroid && isStandalone) && (
  isStandalone
  || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
const REDIRECT_PENDING = 'mk-google-redirect';

function storage(action) {
  try { return action(window.sessionStorage); } catch { return null; }
}

function signInWithGoogle() {
  if (!useGoogleRedirect) return signInWithPopup(auth, googleProvider, browserPopupRedirectResolver);
  storage(store => store.setItem(REDIRECT_PENDING, '1'));
  return signInWithRedirect(auth, googleProvider, browserPopupRedirectResolver);
}

function isGmailAddress(value) {
  return value.trim().toLowerCase().includes('gmail');
}


// Inline mark instead of react-icons, which would otherwise ship on the landing page.
function GoogleMark({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" width="1em" height="1em" aria-hidden="true" focusable="false">
      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z" />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z" />
    </svg>
  );
}

export default function LoginForm() {
  const { t, getRaw } = useI18n();
  const [isRegister, setIsRegister] = useState(false);
  // Keep the expanded registration form in view without opening the keyboard.
  useEffect(() => {
    if (isRegister && window.matchMedia('(max-width: 1023px)').matches) {
      document.getElementById('login-form')?.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  }, [isRegister]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocusedState] = useState(null);
  // Mirror focus into the 3D scene channel — the kart "revs" while you type
  const setFocused = field => {
    setFocusedState(field);
    sceneState.focus = field;
  };
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const termsShakeControls = useAnimationControls();
  const shouldUseGoogleSignup = isRegister && isGmailAddress(email);
  const spotlightX = useMotionValue(-300);
  const spotlightY = useMotionValue(-300);
  const spotlight = useMotionTemplate`radial-gradient(300px circle at ${spotlightX}px ${spotlightY}px, var(--accent-glow), transparent 65%)`;
  const handleCardMouse = e => {
    if (isMobileDevice) return;
    const rect = e.currentTarget.getBoundingClientRect();
    spotlightX.set(e.clientX - rect.left);
    spotlightY.set(e.clientY - rect.top);
  };
  const authErrors = getRaw('auth.errors') || {};

  const getAuthError = err => {
    const code = err?.code || '';
    return authErrors[code] || authErrors.default || t('Wystąpił błąd. Spróbuj ponownie.');
  };

  const validateRegistrationConsent = () => {
    if (!isRegister || termsAccepted) {
      setTermsError(false);
      return true;
    }

    setTermsError(true);
    termsShakeControls.start({ x: [0, -10, 10, -7, 7, 0], transition: { duration: 0.35 } });
    return false;
  };

  // Auth skips the popup resolver at startup, so load Google's auth iframe here.
  // A popup must open straight from the tap, so the iframe has to be ready
  // before it; this also picks up the result of a Google redirect.
  useEffect(() => {
    const returning = storage(store => store.getItem(REDIRECT_PENDING)) === '1';
    storage(store => store.removeItem(REDIRECT_PENDING));
    if (returning) setLoading(true);
    getRedirectResult(auth, browserPopupRedirectResolver)
      .then(result => {
        if (result) sceneState.pulse = 'success';
        // Back from Google without a result: say so instead of a silent login page.
        else if (returning && !auth.currentUser) {
          setError(getAuthError(null));
          sceneState.pulse = 'error';
        }
      })
      .catch(err => {
        if (!returning) return;
        setError(getAuthError(err));
        sceneState.pulse = 'error';
      })
      .finally(() => { if (returning) setLoading(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(s => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    kickScene(3);

    if (!validateRegistrationConsent()) return;

    if (isRegister && isGmailAddress(email)) {
      setLoading(true);
      try {
        await signInWithGoogle();
      } catch (err) {
        setError(getAuthError(err));
      } finally {
        setLoading(false);
      }
      return;
    }

    if (isRegister && password !== confirmPassword) {
      setError(t('Hasła nie są takie same'));
      return;
    }
    setLoading(true);
    try {
      if (isRegister) {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(result.user);
        await signOut(auth);
        setAwaitingVerification(true);
        setResendCooldown(60);
      } else {
        const result = await signInWithEmailAndPassword(auth, email, password);
        if (!result.user.emailVerified) {
          await signOut(auth);
          setError(t('Potwierdź adres e-mail przed zalogowaniem. Sprawdź skrzynkę pocztową.'));
          sceneState.pulse = 'error';
          return;
        }
        sceneState.pulse = 'success';
      }
    } catch (err) {
      setError(getAuthError(err));
      sceneState.pulse = 'error';
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || loading) return;
    setLoading(true);
    try {
      setError('');
      const result = await signInWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(result.user);
      await signOut(auth);
      setResendCooldown(60);
    } catch (err) {
      setError(t('Nie udało się wysłać e-maila. Spróbuj ponownie.'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError(t('Wpisz adres e-mail, aby zresetować hasło.'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await sendPasswordResetEmail(auth, email);
      setResetEmailSent(true);
    } catch (err) {
      setError(getAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = async () => {
    setError('');
    kickScene(3);
    if (!validateRegistrationConsent()) return;
    setLoading(true);
    try {
      await signInWithGoogle();
      sceneState.pulse = 'success';
    } catch (err) {
      setError(getAuthError(err));
      sceneState.pulse = 'error';
    } finally {
      setLoading(false);
    }
  };

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;

  return (
    <div
      onMouseMove={handleCardMouse}
      id="login-form" className="login-form-card glass-card p-7 sm:p-8 relative overflow-hidden"
    >
      {/* Cursor spotlight (desktop only) */}
      {!isMobileDevice && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-0"
          style={{ background: spotlight }}
        />
      )}

      {/* Animated racing-light accent bar */}
      <div className="absolute top-0 left-0 right-0 h-[3px] overflow-hidden rounded-t-2xl z-[1]">
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(90deg, transparent, var(--accent) 45%, #6ee7b7 55%, transparent)', opacity: 0.4 }}
        />
        {!isMobileDevice && (
          <motion.div
            aria-hidden
            className="absolute top-0 h-full w-1/3"
            style={{ background: 'linear-gradient(90deg, transparent, #fff, transparent)', filter: 'blur(2px)', opacity: 0.7 }}
            animate={{ x: ['-120%', '420%'] }}
            transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut', repeatDelay: 1 }}
          />
        )}
      </div>

      <div className="relative z-10">
      {awaitingVerification ? (
        <motion.div
          key="verify"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center text-center py-4 gap-5"
        >
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.25)' }}
          >
            <MailCheck size={30} style={{ color: 'var(--accent)' }} />
          </div>

          <div>
            <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
              {t('Sprawdź skrzynkę mailową')}
            </h2>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {t('Wysłaliśmy link weryfikacyjny na')}
            </p>
            <p className="text-sm font-semibold mt-0.5" style={{ color: 'var(--accent)' }}>
              {email}
            </p>
            <p className="text-xs mt-3 leading-relaxed" style={{ color: 'var(--text-muted)' }}>
              {t('Kliknij link w e-mailu, a następnie zaloguj się na swoje konto. Sprawdź też folder Spam.')}
            </p>
          </div>

          <AnimatePresence>
            {error && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="text-red-500 text-xs bg-red-500/10 rounded-lg py-2 px-3 w-full"
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          <motion.button
            whileTap={{ scale: 0.97 }}
            type="button"
            onClick={handleResend}
            disabled={resendCooldown > 0 || loading}
            className="btn-secondary w-full flex items-center justify-center gap-2 !py-3 text-sm font-semibold"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {resendCooldown > 0 ? t('Wyślij ponownie ({{seconds}}s)', { seconds: resendCooldown }) : t('Wyślij ponownie')}
          </motion.button>

          <button
            type="button"
            onClick={() => {
              setAwaitingVerification(false);
              setIsRegister(false);
              setError('');
              setPassword('');
              setConfirmPassword('');
            }}
            className="flex items-center gap-1.5 text-xs font-semibold py-1"
            style={{ color: 'var(--text-muted)' }}
          >
            <ArrowLeft size={13} /> {t('Wróć do logowania')}
          </button>
        </motion.div>
      ) : (
        <>
      <header className="login-form-heading">
        <div><p><span className="login-heading-desktop">MYKART / {isRegister ? '02' : '01'}</span><span className="login-heading-mobile">{t('loginMobile.access')}</span></p><h2>{isRegister ? t('Załóż darmowe konto') : t('Zaloguj się')}</h2></div>
        <span className="login-pass-mark" aria-hidden="true"><Flag size={15}/>{isRegister ? '02' : '01'}</span>
      </header>
      {/* Tab switcher */}
      <div className="login-auth-tabs flex gap-1 p-1 rounded-xl mb-6" style={{ background: 'var(--surface-100)' }}>
        {[
          { id: false, label: t('Logowanie'), icon: LogIn },
          { id: true, label: t('Rejestracja'), icon: UserPlus },
        ].map(tab => (
          <motion.button
            key={String(tab.id)}
            type="button" disabled={loading} aria-pressed={isRegister === tab.id}
            onClick={() => { setIsRegister(tab.id); setError(''); setTermsError(false); }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold transition-all relative"
            style={{
              color: isRegister === tab.id ? 'var(--accent)' : 'var(--text-muted)',
            }}
          >
            {isRegister === tab.id && (
              <motion.div
                layoutId="activeTab"
                className="absolute inset-0 rounded-lg shadow-sm"
                style={{ background: 'var(--glass-bg)' }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
            <span className="relative z-10 flex items-center gap-1.5">
              <tab.icon size={13} /> {tab.label}
            </span>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 overflow-hidden"
          >
            <p role="alert" className="text-red-500 text-xs text-center bg-red-500/10 rounded-lg py-2.5 px-3">
              {error}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <form onSubmit={handleSubmit} className="space-y-3.5">
        {/* Email */}
        <div className="login-field">
        <label className="login-field-label" htmlFor="login-email">{t('Email')}</label>
        <div className="relative">
          <Mail
            size={16}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 transition-colors"
            style={{ color: focused === 'email' ? 'var(--accent)' : 'var(--text-muted)' }}
          />
          <input
            id="login-email" aria-label={t('Email')} autoComplete="email"
            type="email"
            placeholder={t('Email')}
            value={email}
            onChange={e => { setEmail(e.target.value); kickScene(1); }}
            onFocus={() => setFocused('email')}
            onBlur={() => setFocused(null)}
            required
            disabled={loading}
            className={`input-modern !pl-10 ${emailValid ? '!pr-9' : ''}`}
          />
          <AnimatePresence>
            {emailValid && (
              <span
                className="absolute right-3 top-1/2 -translate-y-1/2 flex"
                style={{ color: 'var(--accent)' }}
              >
                <motion.span
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  className="flex"
                >
                  <Check size={15} />
                </motion.span>
              </span>
            )}
          </AnimatePresence>
        </div>

        </div>
        {/* Gmail registration uses Google, so no local password is needed. */}
        {!shouldUseGoogleSignup && <>
        {/* Password */}
        <div className="login-field">
        <label className="login-field-label" htmlFor="login-password">{t('Hasło')}</label>
        <div className="relative">
          <Lock
            size={16}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 transition-colors"
            style={{ color: focused === 'password' ? 'var(--accent)' : 'var(--text-muted)' }}
          />
          <input
            type={showPassword ? 'text' : 'password'}
            id="login-password" aria-label={t('Hasło')} autoComplete={isRegister ? 'new-password' : 'current-password'}
            placeholder={t('Hasło')}
            value={password}
            onChange={e => { setPassword(e.target.value); kickScene(1); }}
            onFocus={() => setFocused('password')}
            onBlur={() => setFocused(null)}
            required={!shouldUseGoogleSignup}
            disabled={loading}
            className="input-modern !pl-10 !pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(s => !s)}
            className="login-password-toggle absolute right-3 top-1/2 -translate-y-1/2 p-0.5"
            style={{ color: 'var(--text-muted)' }}
            aria-label={showPassword ? t('Ukryj hasło') : t('Pokaż hasło')} aria-pressed={showPassword}
          >
            {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>

        </div>
        {/* Password strength (register only) */}
        <AnimatePresence>
          {isRegister && password.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <PasswordStrength password={password} t={t} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Confirm password */}
        <AnimatePresence>
          {isRegister && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <label className="login-field-label" htmlFor="login-confirm">{t('Powtórz hasło')}</label>
              <div className="relative">
                <Lock
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: focused === 'confirm' ? 'var(--accent)' : 'var(--text-muted)' }}
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="login-confirm" aria-label={t('Powtórz hasło')} autoComplete="new-password"
                  placeholder={t('Powtórz hasło')}
                  value={confirmPassword}
                  onChange={e => { setConfirmPassword(e.target.value); kickScene(1); }}
                  onFocus={() => setFocused('confirm')}
                  onBlur={() => setFocused(null)}
                  required={!shouldUseGoogleSignup}
                  disabled={loading}
                  className={`input-modern !pl-10 ${confirmPassword.length > 0 ? '!pr-9' : ''}`}
                />
                {confirmPassword.length > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: passwordsMatch ? 'var(--accent)' : '#ef4444' }}
                  >
                    {passwordsMatch ? <Check size={15} /> : <X size={15} />}
                  </motion.span>
                )}
              </div>
              {confirmPassword.length > 0 && (
                <p
                  className="text-[11px] mt-1.5 flex items-center gap-1 px-1"
                  style={{ color: passwordsMatch ? 'var(--accent)' : '#ef4444' }}
                >
                  {passwordsMatch ? t('Hasła są zgodne') : t('Hasła nie są takie same')}
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        </>}

        {shouldUseGoogleSignup && (
          <p className="text-xs text-center -mt-1" style={{ color: 'var(--text-muted)' }}>
            {t('Dla Gmaila utworzymy konto przez bezpieczne logowanie Google.')}
          </p>
        )}

        <AnimatePresence>
          {isRegister && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <motion.div
                animate={termsShakeControls}
                className="flex items-start gap-3 rounded-xl px-3 py-2.5 text-xs leading-relaxed border"
                style={{
                  background: termsError ? 'rgba(239,68,68,0.10)' : 'var(--surface-100)',
                  color: 'var(--text-secondary)',
                  borderColor: termsError ? 'rgba(239,68,68,0.45)' : 'transparent',
                }}
              >
                <input
                  type="checkbox" aria-label={t('Akceptuję regulamin i politykę prywatności')} aria-invalid={termsError}
                  checked={termsAccepted}
                  onChange={e => {
                    const checked = e.target.checked;
                    setTermsAccepted(checked);
                    if (checked) {
                      setTermsError(false);
                      termsShakeControls.set({ x: 0 });
                    }
                  }}
                  disabled={loading}
                  className="mt-0.5 h-4 w-4 rounded border-white/20"
                />
                <span>
                  {t('Akceptuję')}{' '}
                  <Link to="/regulamin" className="font-semibold underline" style={{ color: 'var(--accent)' }}>
                    {t('regulamin')}
                  </Link>{' '}
                  {t('oraz')}{' '}
                  <Link to="/polityka-prywatnosci" className="font-semibold underline" style={{ color: 'var(--accent)' }}>
                    {t('politykę prywatności')}
                  </Link>
                  .
                </span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Forgot password */}
        <AnimatePresence>
          {!isRegister && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              {resetEmailSent ? (
                <p className="text-xs text-center py-1" style={{ color: 'var(--accent)' }}>
                  {t('Link do resetowania hasła wysłany! Sprawdź skrzynkę.')}
                </p>
              ) : (
                <p className="text-xs text-right -mt-1">
                  <button type="button" disabled={loading}
                    onClick={handleForgotPassword}
                    className="cursor-pointer hover:underline font-medium"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    {t('Zapomniałem hasła')}
                  </button>
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Submit */}
        <motion.button
          whileHover={isMobileDevice ? undefined : { scale: 1.015 }}
          whileTap={isMobileDevice ? undefined : { scale: 0.985 }}
          type="submit"
          disabled={loading}
          className="login-submit btn-primary group/btn w-full flex items-center justify-center gap-2 !py-3 text-sm font-bold relative overflow-hidden"
        >
          {/* Shine sweep on hover */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700 ease-out"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)' }}
          />
          <span className="relative z-10 flex items-center justify-center gap-2">
            {loading ? (
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                {shouldUseGoogleSignup ? <GoogleMark /> : isRegister ? <UserPlus size={16} /> : <LogIn size={16} />}
                {shouldUseGoogleSignup ? t('Kontynuuj z Google') : isRegister ? t('Utwórz konto') : t('Zaloguj się')}
                <ChevronRight size={16} className="ml-1 transition-transform group-hover/btn:translate-x-1" />
              </>
            )}
          </span>
        </motion.button>
      </form>

      {!shouldUseGoogleSignup && <>
      {/* Divider */}
      <div className="login-auth-divider flex items-center gap-3 my-5">
        <div className="flex-1 h-px" style={{ background: 'var(--glass-border)' }} />
        <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
           {t('lub')}
        </span>
        <div className="flex-1 h-px" style={{ background: 'var(--glass-border)' }} />
      </div>

      {/* Google */}
      <motion.button
        whileHover={isMobileDevice ? undefined : { scale: 1.015 }}
        whileTap={isMobileDevice ? undefined : { scale: 0.985 }}
        type="button"
        onClick={googleLogin}
        disabled={loading}
        className="login-google btn-secondary w-full flex items-center justify-center gap-2.5 !py-3 text-sm font-semibold"
      >
        <GoogleMark className="text-lg" />
        {t('Kontynuuj z Google')}
      </motion.button>

      </>}

      {/* Switch hint */}
      <p className="login-switch-hint text-xs text-center mt-5" style={{ color: 'var(--text-muted)' }}>
        {isRegister ? t('Masz już konto?') : t('Nie masz konta?')}{' '}
        <button type="button" disabled={loading}
          onClick={() => { setIsRegister(!isRegister); setError(''); setTermsError(false); }}
          className="font-semibold cursor-pointer hover:underline"
          style={{ color: 'var(--accent)' }}
        >
          {isRegister ? t('Zaloguj się') : t('Załóż je!')}
        </button>
      </p>

      <div className="login-marketing-link text-xs text-center mt-3" style={{ color: 'var(--text-muted)' }}>
        <p>{t('auth.marketing.prompt')}</p>
        <Link
          to="/dla-kartingowcow"
          className="group inline-flex items-center gap-1.5 font-semibold hover:underline mt-1.5"
          style={{ color: 'var(--accent)' }}
        >
          {t('auth.marketing.link')}
          <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </div>
      </>
      )}
      </div>
    </div>
  );
}
